// LOGIN URL UTIL

const productionBaseDomains = [
	'engros.ae',
	'engros.ai',
	'engros.app',
	'engros.at',
	'engros.dev',
	'engros.es',
	'engros.fi',
	'engros.in',
	'engros.io',
	'engros.it',
	'engros.nz',
	'engros.pt',
	'engros.se',
	'engros.uk',
	'partihandel.io',
	'partihandel.se',
	'partihanteraren.com',
	'pifa.se',
	'lotusfoodservice.se',
	'swecofoodservice.se',
	'swecofoodservice.com',
];

const getBaseDomain = host => {
	const matchedDomain = productionBaseDomains.find(domain => host.endsWith(domain));
	return matchedDomain || null;
};

const getSubdomainPrefix = (host, baseDomain) => {
	if (!baseDomain) return '';

	if (host === baseDomain) return '';

	const prefix = host.replace(`.${baseDomain}`, '');

	const parts = prefix.split('.');

	// If first part is a service subdomain (id, app, api), strip it
	if (parts.length > 0 && ['id', 'app', 'api'].includes(parts[0])) {
		return parts.slice(1).join('.');
	}

	return prefix;
};

const getEnvironmentAndDomain = () => {
	const host = window.location.host;

	if (host.includes('pifa.dev')) {
		return {environment: 'sandbox', domain: 'pifa.dev', subdomain: ''};
	}

	if (host.includes('id.localhost:8080') || host.includes('localhost:8080')) {
		return {
			environment: 'localproduction',
			domain: 'localhost:8080',
			subdomain: '',
		};
	}

	if (host.includes('localhost')) {
		return {environment: 'development', domain: 'localhost', subdomain: ''};
	}

	const baseDomain = getBaseDomain(host);

	if (baseDomain) {
		const subdomain = getSubdomainPrefix(host, baseDomain);
		return {environment: 'production', domain: baseDomain, subdomain};
	}

	return {environment: 'development', domain: 'localhost', subdomain: ''};
};

const {environment, domain, subdomain} = getEnvironmentAndDomain();

// Customer-controlled domains - mirrors pifa-loadbalancer/server.js's
// `customerDomains` list. Used to hide Pifa-branded chrome (e.g. the
// partihandel.se social media icons) that shouldn't appear on a tenant's
// own domain. Keep in sync with server.js if a new customer domain is added.
const customerDomains = ['lotusfoodservice.se', 'swecofoodservice.se', 'swecofoodservice.com'];

const isCustomerControlledDomain = (() => {
	const host = window.location.host.split(':')[0];
	return customerDomains.some(d => host === d || host.endsWith(`.${d}`));
})();

// Partihandel's own social media icons (Instagram/LinkedIn/WhatsApp/TikTok)
// must only show on Pifa's own brand domains — every other domain is a
// tenant's branded login page and shouldn't carry Pifa's social links.
// Non-production environments (local dev, sandbox) are exempted so
// developers can still see/test the icons without needing one of these
// exact domains.
//
// `domain` here is the *base* domain, so matching it alone was not enough:
// every tenant on a partihandel.se subdomain (id.eth.partihandel.se,
// id.vfw.partihandel.se, ...) resolved to domain === 'partihandel.se' and so
// carried Pifa's social links on its own branded login page. Only the apex
// qualifies, which means `subdomain` — the tenant slug, '' on the apex, see
// getSubdomainPrefix above — must also be empty. Tenants on a customer-owned
// domain were already excluded, since those domains aren't in this list.
const PIFA_BRAND_DOMAINS = ['pifa.se', 'engros.ai', 'partihandel.se'];
const showSocialIcons = environment !== 'production' || (PIFA_BRAND_DOMAINS.includes(domain) && !subdomain);

const getApiBaseUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://api.${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://api.${subdomainPart}${domain}`;
		default:
			return 'http://localhost:8000';
	}
};

const getAppUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://app.${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://app.${subdomainPart}${domain}`;
		case 'localproduction':
			return 'http://app.localhost:8080';
		default:
			return 'http://localhost:3000';
	}
};

const getStartUrl = () => {
	const subdomainPart = subdomain ? `${subdomain}.` : '';

	switch (environment) {
		case 'production':
			return `https://${subdomainPart}${domain}`;
		case 'sandbox':
			return `https://${subdomainPart}${domain}`;
		case 'localproduction':
			return 'http://localhost:8080';
		default:
			return 'http://localhost:1048';
	}
};

export {getApiBaseUrl, getAppUrl, getStartUrl, isCustomerControlledDomain, showSocialIcons};
